import Component from '@glimmer/component';
import { action } from '@ember/object';
import { cancel, later, next } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';
import { create, enforce, omitWhen, test } from 'vest';
import {
  DEFAULT_PAGE_TYPE,
  PAGE_TYPE_INFORMATION,
  PAGE_TYPE_SHOP,
  isSupportedPageType,
} from 'admin-panel/constants/page-types';
import {
  composePageContent,
  splitPageContentTranslations,
} from 'admin-panel/utils/page-header-image';
import {
  composePageHeaderTitle,
  pageHeaderTitleModeFor,
  splitPageHeaderTitleTranslations,
} from 'admin-panel/utils/page-header-title';

import type FormState from 'admin-panel/resources/form-state';
import type IntlService from 'ember-intl/services/intl';
import type { TranslatedField } from 'admin-panel/constants/types';
import type RoomModel from 'admin-panel/models/room';
import type { WysiwygEditorLinkDestinations } from 'admin-panel/utils/wysiwyg-editor/types';
import type {
  PageHeaderImageBlock,
  PageHeaderImagePrefixTranslations,
  PageHeaderImageTranslations,
} from 'admin-panel/utils/page-header-image';
import type {
  PageHeaderTitleMode,
  PageHeaderTitleModeTranslations,
  PageHeaderTitlePrefixTranslations,
  PageHeaderTitleTranslations,
} from 'admin-panel/utils/page-header-title';

export type VisibilityMode = 'all' | 'specific' | 'nowhere';

// Keep this duration aligned with the `duration-200` transition classes in
// the template so the switch stops animating after the reveal has settled.
const HEADER_TITLE_TRANSITION_MS = 200;

export type PageFormData = {
  cleanType: string;
  visibilityMode: VisibilityMode;
  roomIds: number[];
  titleTranslations: TranslatedField;
  contentTranslations: TranslatedField;
  headerImageTranslations: PageHeaderImageTranslations;
  headerImagePrefixTranslations: PageHeaderImagePrefixTranslations;
  isHeaderTitleSplit: boolean;
  headerTitleModeTranslations: PageHeaderTitleModeTranslations;
  headerTitleTranslations: PageHeaderTitleTranslations;
  headerTitlePrefixTranslations: PageHeaderTitlePrefixTranslations;
};

type PageFormInitialValuesSource = {
  active?: boolean;
  cleanType?: string;
  contentTranslations?: TranslatedField;
  roomIds?: number[];
  titleTranslations?: TranslatedField;
};

export type PageFormContentContext = {
  pageIdentifier: string;
  localeKey: string;
};

export function withHeaderImageTranslation(
  translations: PageHeaderImageTranslations,
  localeKey: string,
  value: PageHeaderImageBlock | null
) {
  const nextTranslations = { ...translations };

  if (value) {
    nextTranslations[localeKey] = value;
  } else {
    delete nextTranslations[localeKey];
  }

  return nextTranslations;
}

export function withHeaderTitleMode(
  translations: PageHeaderTitleModeTranslations,
  localeKey: string,
  mode: PageHeaderTitleMode
) {
  const nextTranslations = { ...translations };

  if (mode === 'inherited') {
    delete nextTranslations[localeKey];
  } else {
    nextTranslations[localeKey] = mode;
  }

  return nextTranslations;
}

export function withHeaderTitleTranslation(
  translations: PageHeaderTitleTranslations,
  localeKey: string,
  value: string
) {
  return { ...translations, [localeKey]: value };
}

export function setSeparateHeaderTitleMode(
  form: FormState<PageFormData>,
  localeKey: string,
  isSeparate: boolean
) {
  const { headerTitleTranslations } = form.values;

  if (
    isSeparate &&
    !Object.prototype.hasOwnProperty.call(headerTitleTranslations, localeKey)
  ) {
    form.setFieldValue(
      'headerTitleTranslations',
      withHeaderTitleTranslation(
        headerTitleTranslations,
        localeKey,
        localeValue(form.values.titleTranslations, localeKey)
      )
    );
  }

  form.setFieldValue(
    'headerTitleModeTranslations',
    withHeaderTitleMode(
      form.values.headerTitleModeTranslations,
      localeKey,
      isSeparate ? 'custom' : 'inherited'
    )
  );
}

export function initialPageFormValues(
  source: PageFormInitialValuesSource
): PageFormData {
  const cleanType = pageTypeForForm(source.cleanType);
  const isHeaderTitleSplit = isSupportedPageType(cleanType);
  const titleTranslations = source.titleTranslations ?? {};
  const pageContent = splitPageContentTranslations(
    source.contentTranslations ?? {}
  );
  const headerTitle = isHeaderTitleSplit
    ? splitPageHeaderTitleTranslations(
        pageContent.contentTranslations,
        titleTranslations
      )
    : {
        contentTranslations: pageContent.contentTranslations,
        headerTitleModeTranslations: {},
        headerTitlePrefixTranslations: {},
        headerTitleTranslations: {},
      };

  return {
    cleanType,
    visibilityMode: visibilityModeForPage(source.active, source.roomIds),
    roomIds: [...(source.roomIds ?? [])],
    titleTranslations,
    contentTranslations: headerTitle.contentTranslations,
    headerImageTranslations: pageContent.headerImageTranslations,
    headerImagePrefixTranslations: pageContent.headerImagePrefixTranslations,
    isHeaderTitleSplit,
    headerTitleModeTranslations: headerTitle.headerTitleModeTranslations,
    headerTitleTranslations: headerTitle.headerTitleTranslations,
    headerTitlePrefixTranslations: headerTitle.headerTitlePrefixTranslations,
  };
}

export function savedPageFormValues(
  source: PageFormInitialValuesSource,
  draft: PageFormData
): PageFormData {
  const values = initialPageFormValues(source);

  if (!values.isHeaderTitleSplit || !draft.isHeaderTitleSplit) return values;

  const headerTitleModeTranslations = {
    ...values.headerTitleModeTranslations,
  };
  const headerTitleTranslations = { ...values.headerTitleTranslations };

  Object.entries(draft.headerTitleModeTranslations).forEach(
    ([localeKey, mode]) => {
      if (mode !== 'custom') return;

      headerTitleModeTranslations[localeKey] = mode;
      headerTitleTranslations[localeKey] =
        draft.headerTitleTranslations[localeKey] ?? '';
    }
  );

  return {
    ...values,
    headerTitleModeTranslations,
    headerTitleTranslations,
  };
}

export function composeFormContent(
  currentTranslations: TranslatedField,
  values: PageFormData,
  localeKey: string
): TranslatedField {
  const nextTranslations = { ...currentTranslations };
  const headerImage = values.headerImageTranslations[localeKey];
  const prefixHtml = values.headerImagePrefixTranslations[localeKey] ?? '';
  const headerTitleMode =
    values.isHeaderTitleSplit && isSupportedPageType(values.cleanType)
      ? pageHeaderTitleModeFor(values.headerTitleModeTranslations, localeKey)
      : 'unmanaged';
  const navigationTitle = values.titleTranslations[localeKey] ?? '';
  const customHeaderTitle = values.headerTitleTranslations[localeKey] ?? '';
  const headerTitlePrefixHtml =
    values.headerTitlePrefixTranslations[localeKey] ?? '';
  const hasContent = Object.prototype.hasOwnProperty.call(
    values.contentTranslations,
    localeKey
  );
  const hasNavigationTitle = Object.prototype.hasOwnProperty.call(
    values.titleTranslations,
    localeKey
  );

  if (
    hasContent ||
    headerImage ||
    prefixHtml ||
    (headerTitleMode !== 'unmanaged' && hasNavigationTitle)
  ) {
    const content = composePageHeaderTitle(
      values.contentTranslations[localeKey],
      navigationTitle,
      headerTitleMode,
      customHeaderTitle,
      headerTitlePrefixHtml
    );

    nextTranslations[localeKey] = composePageContent(
      content,
      headerImage,
      prefixHtml
    );
  }

  return nextTranslations;
}

interface PanelCategoryPageFormArgs {
  form: FormState<PageFormData>;
  contentLocaleKey: string;
  pageIdentifier: string;
  linkDestinations?: WysiwygEditorLinkDestinations;
  roomPickerContextKey?: string;
  isPageTypeDisabled?: boolean;
  rooms?: RoomModel[];
  roomsLoading?: boolean;
  roomsLoadFailed?: boolean;
  shouldFocusTitle?: boolean;
  onTitleChange: (value: string) => void;
  onContentChange: (context: PageFormContentContext, value: string) => void;
  onHeaderImageChange: (value: PageHeaderImageBlock | null) => void;
  canShareHeaderImage?: boolean;
  isHeaderImageShared?: boolean;
  hasPendingHeaderImageRemoval?: boolean;
  isHeaderImageSharingLoading?: boolean;
  headerImageSharingLoadFailed?: boolean;
  onEnsureHeaderImageSharing?: () => Promise<boolean>;
  onApplyHeaderImageOptions?: (
    value: PageHeaderImageBlock | null,
    isShared: boolean
  ) => void;
  getHeaderImageReplacementCount?: (
    value: PageHeaderImageBlock | null
  ) => number;
  onHeaderImageCroppingChange: (isCropping: boolean) => void;
  onHeaderTitleChange: (value: string) => void;
  onSeparateHeaderTitleChange: (isSeparate: boolean) => void;
  onPageTypeChange: (value: string) => void;
  onVisibilityModeChange?: (mode: VisibilityMode) => void;
  onRoomIdsChange?: (roomIds: number[]) => void;
  onRetryRooms?: () => void;
}

export function pageTypeForForm(value: string | undefined) {
  return value ?? DEFAULT_PAGE_TYPE;
}

export function localeValue(translations: TranslatedField, localeKey: string) {
  return translations[localeKey] ?? '';
}

export function visibilityModeForPage(
  active: boolean | undefined,
  roomIds: number[] | undefined
): VisibilityMode {
  if (active === false) return 'nowhere';

  return roomIds?.length ? 'specific' : 'all';
}

export function persistedVisibilityFor(values: PageFormData) {
  return {
    active: values.visibilityMode !== 'nowhere',
    roomIds: values.visibilityMode === 'all' ? [] : values.roomIds,
  };
}

export const pageFormSuite = create<
  (
    data: PageFormData,
    options: { t: IntlService['t']; contentLocaleKey: string }
  ) => void
>('PageForm', (data, { t, contentLocaleKey }) => {
  test('titleTranslations', t('validations.required'), () => {
    enforce(data.titleTranslations[contentLocaleKey]?.trim()).isTruthy();
  });

  omitWhen(data.visibilityMode !== 'specific', () => {
    test('roomIds', t('validations.required'), () => {
      enforce(data.roomIds).isNotEmpty();
    });
  });
});

export default class PanelCategoryPageFormComponent extends Component<PanelCategoryPageFormArgs> {
  @tracked
  isEditorMounted = true;

  @tracked
  shouldAnimateHeaderTitleTransition = false;

  @tracked
  headerTitleTransitionKey = 0;

  headerTitleTransitionTimer: ReturnType<typeof later> | null = null;

  isHeaderTitleLeavePending = false;

  shouldFocusHeaderTitleOnInsert = false;

  willDestroy() {
    this.cancelHeaderTitleTransition();
    super.willDestroy();
  }

  private cancelHeaderTitleTransition() {
    if (!this.headerTitleTransitionTimer) return;

    cancel(this.headerTitleTransitionTimer);
    this.headerTitleTransitionTimer = null;
  }

  get pageTypeInformation() {
    return PAGE_TYPE_INFORMATION;
  }

  get pageTypeShop() {
    return PAGE_TYPE_SHOP;
  }

  get currentTitle() {
    return localeValue(
      this.args.form.values.titleTranslations,
      this.args.contentLocaleKey
    );
  }

  get currentContent() {
    return localeValue(
      this.args.form.values.contentTranslations,
      this.args.contentLocaleKey
    );
  }

  get currentHeaderImage() {
    return (
      this.args.form.values.headerImageTranslations[
        this.args.contentLocaleKey
      ] ?? null
    );
  }

  get currentHeaderTitleMode() {
    const { cleanType, headerTitleModeTranslations, isHeaderTitleSplit } =
      this.args.form.values;

    return isHeaderTitleSplit && isSupportedPageType(cleanType)
      ? pageHeaderTitleModeFor(
          headerTitleModeTranslations,
          this.args.contentLocaleKey
        )
      : 'unmanaged';
  }

  get currentHeaderTitle() {
    return (
      this.args.form.values.headerTitleTranslations[
        this.args.contentLocaleKey
      ] ?? ''
    );
  }

  get isHeaderTitleManaged() {
    return this.currentHeaderTitleMode !== 'unmanaged';
  }

  get hasSeparateHeaderTitle() {
    return this.currentHeaderTitleMode === 'custom';
  }

  get headerTitleSwitchClass() {
    return this.shouldAnimateHeaderTitleTransition
      ? ''
      : 'transition-none [&>span]:transition-none';
  }

  get titleTouched() {
    return Boolean(this.args.form.touched.titleTranslations);
  }

  get titleErrors() {
    return this.args.form.errors.titleTranslations as string[] | undefined;
  }

  get contentTouched() {
    return Boolean(this.args.form.touched.contentTranslations);
  }

  get contentErrors() {
    return this.args.form.errors.contentTranslations as string[] | undefined;
  }

  get titleKey() {
    return `titleTranslations.${this.args.contentLocaleKey}`;
  }

  get headerTitleKey() {
    return `headerTitleTranslations.${this.args.contentLocaleKey}`;
  }

  get rooms() {
    return this.args.rooms ?? [];
  }

  get editorInstanceKey() {
    return `${this.args.pageIdentifier}:${this.args.contentLocaleKey}`;
  }

  get roomPickerContextKey() {
    return this.args.roomPickerContextKey ?? this.args.pageIdentifier;
  }

  get unsupportedPageType() {
    const { cleanType } = this.args.form.values;

    return isSupportedPageType(cleanType) ? null : cleanType;
  }

  @action
  updateTitle(event: Event) {
    const input = event.currentTarget;
    if (!(input instanceof HTMLInputElement)) return;

    this.args.onTitleChange(input.value);
  }

  @action
  updateHeaderTitle(event: Event) {
    const input = event.currentTarget;
    if (!(input instanceof HTMLInputElement)) return;

    this.args.onHeaderTitleChange(input.value);
  }

  @action
  updateSeparateHeaderTitle(isOn: boolean) {
    this.cancelHeaderTitleTransition();

    if (isOn && this.isHeaderTitleLeavePending) {
      this.headerTitleTransitionKey++;
    }

    this.shouldAnimateHeaderTitleTransition = true;
    this.isHeaderTitleLeavePending = !isOn;
    this.shouldFocusHeaderTitleOnInsert = isOn;
    this.args.onSeparateHeaderTitleChange(isOn);

    this.headerTitleTransitionTimer = later(() => {
      if (this.isDestroying || this.isDestroyed) return;

      this.shouldAnimateHeaderTitleTransition = false;
      this.isHeaderTitleLeavePending = false;
      this.headerTitleTransitionTimer = null;
    }, HEADER_TITLE_TRANSITION_MS);
  }

  @action
  focusHeaderTitleInputOnToggle(element: HTMLInputElement) {
    if (!this.shouldFocusHeaderTitleOnInsert) return;

    this.shouldFocusHeaderTitleOnInsert = false;
    element.focus();
  }

  @action
  updatePageType(event: Event) {
    const select = event.currentTarget;
    if (!(select instanceof HTMLSelectElement)) return;

    this.args.onPageTypeChange(select.value);
  }

  @action
  resetEditorForContext() {
    this.cancelHeaderTitleTransition();

    this.shouldAnimateHeaderTitleTransition = false;
    this.isHeaderTitleLeavePending = false;
    this.shouldFocusHeaderTitleOnInsert = false;
    this.headerTitleTransitionKey++;
    this.isEditorMounted = false;

    next(() => {
      if (this.isDestroying || this.isDestroyed) return;

      this.isEditorMounted = true;
    });
  }

  @action
  focusTitleInputOnCreate(element: HTMLInputElement) {
    if (!this.args.shouldFocusTitle) return;

    next(() => {
      if (this.isDestroying || this.isDestroyed) return;

      element.focus();
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Panel::Category::PageForm': typeof PanelCategoryPageFormComponent;
    'panel/category/page-form': typeof PanelCategoryPageFormComponent;
  }
}
